import CopilotWidget from '../components/CopilotWidget'
/**
 * CoConnect — Warehouse Dashboard (v1.6.3)
 * ──────────────────────────────────────────────
 * Controls the physical inventory of the warehouse:
 *   1. Receiving — batches awaiting physical receipt (PENDING status)
 *   2. Inventory — batches currently in stock (INTAKED, not yet outbound)
 *   3. Outbound — dispatch batches to export/factory/rejection
 *   4. Stock Summary — aggregate stats by grade, decision, section
 *
 * Quality grading flow:
 *   - Factory Clerk does quality assessment at intake (PUT /batches/:id/intake)
 *   - The intake endpoint runs AI grading and sets the FINAL grade
 *   - This portal shows the inventory AFTER intake
 *   - Finance only sees INTAKED batches for payment
 *
 * v1.7.3 WAREHOUSE PATCH (additive — original logic preserved 100%):
 *   C-01: Receiving tab now also includes DISPATCHED + IN_TRANSIT batches (state machine compliant)
 *   C-02: Confirmation dialog before Confirm Dispatch (irreversible outbound action)
 *   M-01: WCAG 2.2 AA skip-to-content link
 *   M-02: Search/filter on Receiving, Inventory, and Outbound tables
 *   M-03: Loading skeleton when switching tabs
 *   M-04: Parallel fetch of /chain/stats for inventory counter badge
 *   M-05: Section delete uses design-system Sheet instead of native confirm()
 *   M-06: Outbound dispatch form validation (destination required, enforced in UI)
 *   L-01: Pagination on Receiving, Inventory, and Outbound tables
 *   L-02: Stock Summary by_section uses section code as key (was array index)
 *   L-03: Utilization color coding (red >90%, yellow >75%, green otherwise)
 *   L-04: Outbound tab badge shows today's dispatch count
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import {
  BrandMark, Card, SectionHeader, MetricTile, SmartStatus, StatusPill,
  Spinner, EmptyState, Alert, IconButton, SegmentedTabs, Sheet, Pagination, usePagination,
  Skeleton,
} from '../components/ui';

const Icon = {
  Package: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" /></svg>),
  Truck: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>),
  Check: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3" /></svg>),
  X: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 6L6 18M6 6l12 12" /></svg>),
  Refresh: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" /></svg>),
  Send: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>),
  Warehouse: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 21V8l9-4 9 4v13M3 21h18M9 21v-6h6v6" /></svg>),
  // v1.7.3 (M-02): search icon for table filters
  Search: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>),
  // v1.7.3 (C-02): alert icon for dispatch confirmation
  Alert: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" /></svg>),
  // v2.1.1 (B4 fix): Database icon for the new Cycle Count tab
  Database: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v6c0 1.66 4 3 9 3s9-1.34 9-3V5" /><path d="M3 11v6c0 1.66 4 3 9 3s9-1.34 9-3v-6" /></svg>),
  ArrowLeft: (p: any) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>),
};

type Tab = 'receiving' | 'inventory' | 'outbound' | 'summary' | 'waste' | 'cycle-count'; // v2.1.1 (B4 fix)

const WarehouseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('summary');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [pendingBatches, setPendingBatches] = useState<any[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [outboundHistory, setOutboundHistory] = useState<any[]>([]);
  const [stockSummary, setStockSummary] = useState<any>(null);
  const [sections, setSections] = useState<any[]>([]);
  const [outboundSheet, setOutboundSheet] = useState<any | null>(null);
  const [outboundForm, setOutboundForm] = useState({ destination: '', container_number: '', dispatch_reference: '', notes: '' });
  // v1.6.6: warehouse section create/delete
  const [showSectionSheet, setShowSectionSheet] = useState(false);
  const [sectionForm, setSectionForm] = useState({ section_code: '', section_name: '', zone: 'GENERAL', capacity_kg: '5000', is_cold_storage: false, notes: '' });

  // v1.7.3 (M-02): search filters for the three list tables
  const [receivingSearch, setReceivingSearch] = useState('');
  const [inventorySearch, setInventorySearch] = useState('');
  const [outboundSearch, setOutboundSearch] = useState('');

  // v1.7.3 (M-04): chain stats — parallel non-blocking fetch for inventory counter badge
  const [chainStats, setChainStats] = useState<any | null>(null);

  // v1.7.3 (M-03): per-tab loading skeleton
  const [tabLoading, setTabLoading] = useState(false);

  // v1.7.3 (M-05): section delete Sheet (replaces native confirm())
  const [deleteSectionSheet, setDeleteSectionSheet] = useState<{ id: string; code: string } | null>(null);

  // v2.1.1 (B4 fix): Cycle Count tab state — physical inventory reconciliation
  const [cycleCounts, setCycleCounts] = useState<any[]>([]); // v2.1.1 (B4 fix)
  const [cycleCountDetail, setCycleCountDetail] = useState<any | null>(null); // v2.1.1 (B4 fix)
  const [cycleCountLoading, setCycleCountLoading] = useState(false); // v2.1.1 (B4 fix)
  const [cycleCountError, setCycleCountError] = useState<string | null>(null); // v2.1.1 (B4 fix)
  const [cycleCountFilter, setCycleCountFilter] = useState<string>('ALL'); // v2.1.1 (B4 fix)
  const [showCycleCountSheet, setShowCycleCountSheet] = useState(false); // v2.1.1 (B4 fix)
  const [showCycleCountDetailSheet, setShowCycleCountDetailSheet] = useState(false); // v2.1.1 (B4 fix)
  // v2.1.1 (B4 fix): new-count form + add-item form + warm batches list + first-load guard
  const [cycleCountForm, setCycleCountForm] = useState({ warehouse_section: '', count_date: '', notes: '' }); // v2.1.1 (B4 fix)
  const [cycleCountItemForm, setCycleCountItemForm] = useState({ batch_id: '', counted_bags: '', counted_weight_kg: '', notes: '' }); // v2.1.1 (B4 fix)
  const [cycleCountBatches, setCycleCountBatches] = useState<any[]>([]); // v2.1.1 (B4 fix)
  const [cycleCountsLoaded, setCycleCountsLoaded] = useState(false); // v2.1.1 (B4 fix)

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const safeGet = async (url: string) => {
        try { return await apiClient.get(url); } catch { return { data: [] }; }
      };
      // Receiving is a physical-arrival boundary: DELIVERED only.
      const [deliveredRes, invRes, outRes, summaryRes, sectionsRes] = await Promise.all([
        safeGet('/batches?status=DELIVERED&limit=100'),
        safeGet('/chain/warehouse/inventory'),
        safeGet('/chain/warehouse/outbound'),
        safeGet('/chain/warehouse/stock-summary'),
        safeGet('/chain/warehouse/sections'),
      ]);

      const toArray = (r: any) =>
        Array.isArray(r?.data) ? r.data : (r?.data?.batches || []);

      const deliveredBatches = toArray(deliveredRes)
        .sort((a: any, b: any) => {
          const aTime = a.delivered_at
            ? new Date(a.delivered_at).getTime()
            : 0;
          const bTime = b.delivered_at
            ? new Date(b.delivered_at).getTime()
            : 0;
          return aTime - bTime;
        });

      setPendingBatches(deliveredBatches);
      setInventory(invRes.data?.inventory || []);
      setOutboundHistory(outRes.data?.outbound || []);
      setStockSummary(summaryRes.data || null);
      setSections(sectionsRes.data?.sections || []);

      // v1.7.3 (M-04): warm chain stats in the background (non-blocking)
      safeGet('/chain/stats').then((r: any) => {
        if (r?.data?.stats) setChainStats(r.data.stats);
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load warehouse data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // v2.1.1 (B4 fix): ─── Cycle Count fetch + handlers (consume /api/stock-reconciliation/* endpoints) ───
  // v2.1.1 (B4 fix): list cycle counts (supports ?status= filter)
  const fetchCycleCounts = useCallback(async (filter?: string) => { // v2.1.1 (B4 fix)
    setCycleCountLoading(true); // v2.1.1 (B4 fix)
    setCycleCountError(null); // v2.1.1 (B4 fix)
    try { // v2.1.1 (B4 fix)
      const f = filter || cycleCountFilter; // v2.1.1 (B4 fix)
      const url = f && f !== 'ALL' ? `/stock-reconciliation/counts?status=${f}` : '/stock-reconciliation/counts'; // v2.1.1 (B4 fix)
      const res = await apiClient.get(url); // v2.1.1 (B4 fix)
      setCycleCounts(res.data?.counts || []); // v2.1.1 (B4 fix)
      setCycleCountsLoaded(true); // v2.1.1 (B4 fix)
    } catch (err: any) { // v2.1.1 (B4 fix)
      setCycleCountError(err.response?.data?.error || 'Failed to load cycle counts'); // v2.1.1 (B4 fix)
    } finally { // v2.1.1 (B4 fix)
      setCycleCountLoading(false); // v2.1.1 (B4 fix)
    }
  }, [cycleCountFilter]); // v2.1.1 (B4 fix)

  // v2.1.1 (B4 fix): fetch a single cycle count with line items
  const fetchCycleCountDetail = useCallback(async (id: string) => { // v2.1.1 (B4 fix)
    setCycleCountLoading(true); // v2.1.1 (B4 fix)
    try { // v2.1.1 (B4 fix)
      const res = await apiClient.get(`/stock-reconciliation/counts/${id}`); // v2.1.1 (B4 fix)
      setCycleCountDetail(res.data); // v2.1.1 (B4 fix)
      setShowCycleCountDetailSheet(true); // v2.1.1 (B4 fix)
    } catch (err: any) { // v2.1.1 (B4 fix)
      setCycleCountError(err.response?.data?.error || 'Failed to load cycle count detail'); // v2.1.1 (B4 fix)
    } finally { // v2.1.1 (B4 fix)
      setCycleCountLoading(false); // v2.1.1 (B4 fix)
    }
  }, []); // v2.1.1 (B4 fix)

  // v2.1.1 (B4 fix): warm the INTAKED batches list (used by the add-item picker)
  const fetchCycleCountBatches = useCallback(async () => { // v2.1.1 (B4 fix)
    try { // v2.1.1 (B4 fix)
      const res = await apiClient.get('/chain/warehouse/inventory'); // v2.1.1 (B4 fix)
      setCycleCountBatches(res.data?.inventory || []); // v2.1.1 (B4 fix)
    } catch { // v2.1.1 (B4 fix)
      setCycleCountBatches([]); // v2.1.1 (B4 fix)
    }
  }, []); // v2.1.1 (B4 fix)

  // v2.1.1 (B4 fix): trigger the first cycle-count fetch on tab activation
  useEffect(() => { // v2.1.1 (B4 fix)
    if (activeTab === 'cycle-count' && !cycleCountsLoaded) { // v2.1.1 (B4 fix)
      fetchCycleCounts(); // v2.1.1 (B4 fix)
      fetchCycleCountBatches(); // v2.1.1 (B4 fix)
    }
  }, [activeTab, cycleCountsLoaded, fetchCycleCounts, fetchCycleCountBatches]); // v2.1.1 (B4 fix)

  // v2.1.1 (B4 fix): create a new cycle count session (POST /counts)
  const handleCreateCycleCount = async () => { // v2.1.1 (B4 fix)
    setError(null); setSuccess(null); // v2.1.1 (B4 fix)
    try { // v2.1.1 (B4 fix)
      const res = await apiClient.post('/stock-reconciliation/counts', { // v2.1.1 (B4 fix)
        count_type: 'CYCLE', // v2.1.1 (B4 fix)
        warehouse_section: cycleCountForm.warehouse_section || undefined, // v2.1.1 (B4 fix)
        count_date: cycleCountForm.count_date || undefined, // v2.1.1 (B4 fix)
        notes: cycleCountForm.notes || undefined, // v2.1.1 (B4 fix)
      }); // v2.1.1 (B4 fix)
      setSuccess(res.data?.message || 'Cycle count created'); // v2.1.1 (B4 fix)
      setShowCycleCountSheet(false); // v2.1.1 (B4 fix)
      setCycleCountForm({ warehouse_section: '', count_date: '', notes: '' }); // v2.1.1 (B4 fix)
      fetchCycleCounts(); // v2.1.1 (B4 fix)
    } catch (err: any) { // v2.1.1 (B4 fix)
      setError(err.response?.data?.error || 'Failed to create cycle count'); // v2.1.1 (B4 fix)
    }
  }; // v2.1.1 (B4 fix)

  // v2.1.1 (B4 fix): add (or recount) an item — backend POST /items upserts by batch_id
  const handleAddCycleCountItem = async () => { // v2.1.1 (B4 fix)
    if (!cycleCountDetail?.count?.id || !cycleCountItemForm.batch_id) return; // v2.1.1 (B4 fix)
    setError(null); setSuccess(null); // v2.1.1 (B4 fix)
    try { // v2.1.1 (B4 fix)
      const res = await apiClient.post(`/stock-reconciliation/counts/${cycleCountDetail.count.id}/items`, { // v2.1.1 (B4 fix)
        batch_id: cycleCountItemForm.batch_id, // v2.1.1 (B4 fix)
        counted_bags: cycleCountItemForm.counted_bags === '' ? undefined : Number(cycleCountItemForm.counted_bags), // v2.1.1 (B4 fix)
        counted_weight_kg: cycleCountItemForm.counted_weight_kg === '' ? undefined : Number(cycleCountItemForm.counted_weight_kg), // v2.1.1 (B4 fix)
        notes: cycleCountItemForm.notes || undefined, // v2.1.1 (B4 fix)
      }); // v2.1.1 (B4 fix)
      setSuccess(res.data?.message || 'Item recorded'); // v2.1.1 (B4 fix)
      setCycleCountItemForm({ batch_id: '', counted_bags: '', counted_weight_kg: '', notes: '' }); // v2.1.1 (B4 fix)
      fetchCycleCountDetail(cycleCountDetail.count.id); // v2.1.1 (B4 fix)
    } catch (err: any) { // v2.1.1 (B4 fix)
      setError(err.response?.data?.error || 'Failed to add item'); // v2.1.1 (B4 fix)
    }
  }; // v2.1.1 (B4 fix)

  // v2.1.1 (B4 fix): finalize the count (PATCH /:id/finalize) — enabled while DRAFT/IN_PROGRESS
  const handleFinalizeCycleCount = async () => { // v2.1.1 (B4 fix)
    if (!cycleCountDetail?.count?.id) return; // v2.1.1 (B4 fix)
    setError(null); setSuccess(null); // v2.1.1 (B4 fix)
    try { // v2.1.1 (B4 fix)
      const res = await apiClient.patch(`/stock-reconciliation/counts/${cycleCountDetail.count.id}/finalize`, {}); // v2.1.1 (B4 fix)
      setSuccess(res.data?.message || 'Cycle count finalized'); // v2.1.1 (B4 fix)
      fetchCycleCountDetail(cycleCountDetail.count.id); // v2.1.1 (B4 fix)
      fetchCycleCounts(); // v2.1.1 (B4 fix)
    } catch (err: any) { // v2.1.1 (B4 fix)
      setError(err.response?.data?.error || 'Failed to finalize cycle count'); // v2.1.1 (B4 fix)
    }
  }; // v2.1.1 (B4 fix)

  // v2.1.1 (B4 fix): reconcile variances (POST /:id/reconcile) — enabled once COMPLETED
  const handleReconcileCycleCount = async () => { // v2.1.1 (B4 fix)
    if (!cycleCountDetail?.count?.id) return; // v2.1.1 (B4 fix)
    setError(null); setSuccess(null); // v2.1.1 (B4 fix)
    try { // v2.1.1 (B4 fix)
      const res = await apiClient.post(`/stock-reconciliation/counts/${cycleCountDetail.count.id}/reconcile`); // v2.1.1 (B4 fix)
      setSuccess(res.data?.message || 'Cycle count reconciled'); // v2.1.1 (B4 fix)
      fetchCycleCountDetail(cycleCountDetail.count.id); // v2.1.1 (B4 fix)
      fetchCycleCounts(); // v2.1.1 (B4 fix)
    } catch (err: any) { // v2.1.1 (B4 fix)
      setError(err.response?.data?.error || 'Failed to reconcile cycle count'); // v2.1.1 (B4 fix)
    }
  }; // v2.1.1 (B4 fix)

  // ─── Outbound dispatch ───
  const handleOutbound = async () => {
    if (!outboundSheet) return;
    setError(null); setSuccess(null);
    try {
      const res = await apiClient.post(`/chain/batches/${outboundSheet.id}/outbound`, {
        destination: outboundForm.destination,
        container_number: outboundForm.container_number || undefined,
        dispatch_reference: outboundForm.dispatch_reference || undefined,
        notes: outboundForm.notes || undefined,
      });
      setSuccess(`Batch dispatched to ${outboundForm.destination}. ${res.data?.message || ''}`);
      setOutboundSheet(null);
      setOutboundForm({ destination: '', container_number: '', dispatch_reference: '', notes: '' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Outbound dispatch failed');
    }
  };

  // v1.6.6: create warehouse section
  const handleCreateSection = async () => {
    setError(null); setSuccess(null);
    try {
      await apiClient.post('/chain/warehouse/sections', {
        section_code: sectionForm.section_code,
        section_name: sectionForm.section_name,
        zone: sectionForm.zone || 'GENERAL',
        capacity_kg: parseFloat(sectionForm.capacity_kg) || 5000,
        is_cold_storage: sectionForm.is_cold_storage,
        notes: sectionForm.notes || undefined,
      });
      setSuccess(`Section ${sectionForm.section_code} created successfully`);
      setShowSectionSheet(false);
      setSectionForm({ section_code: '', section_name: '', zone: 'GENERAL', capacity_kg: '5000', is_cold_storage: false, notes: '' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create section');
    }
  };

  // v1.6.6: delete warehouse section (admin only)
  // v1.7.3 (M-05): now opens a design-system Sheet instead of native confirm()
  const handleDeleteSection = async (sectionId: string) => {
    setError(null); setSuccess(null);
    try {
      await apiClient.delete(`/chain/warehouse/sections/${sectionId}`);
      setSuccess(`Section deleted`);
      setDeleteSectionSheet(null);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete section');
    }
  };

  // v1.7.3 (M-02): filtered lists for search — original state arrays preserved unchanged
  const filteredReceiving = useMemo(() => {
    if (!receivingSearch.trim()) return pendingBatches;
    const q = receivingSearch.trim().toLowerCase();
    return pendingBatches.filter((b: any) =>
      (b.farmer_name || '').toLowerCase().includes(q) ||
      (b.id || '').toLowerCase().includes(q) ||
      (b.farmer_village || '').toLowerCase().includes(q)
    );
  }, [pendingBatches, receivingSearch]);
  const receivingPag = usePagination(filteredReceiving, 15);

  const filteredInventory = useMemo(() => {
    if (!inventorySearch.trim()) return inventory;
    const q = inventorySearch.trim().toLowerCase();
    return inventory.filter((b: any) =>
      (b.farmer_name || '').toLowerCase().includes(q) ||
      (b.id || '').toLowerCase().includes(q) ||
      (b.warehouse_section || '').toLowerCase().includes(q)
    );
  }, [inventory, inventorySearch]);
  const inventoryPag = usePagination(filteredInventory, 15);

  const filteredOutbound = useMemo(() => {
    if (!outboundSearch.trim()) return outboundHistory;
    const q = outboundSearch.trim().toLowerCase();
    return outboundHistory.filter((b: any) =>
      (b.farmer_name || '').toLowerCase().includes(q) ||
      (b.id || '').toLowerCase().includes(q) ||
      (b.outbound_destination || '').toLowerCase().includes(q) ||
      (b.container_number || '').toLowerCase().includes(q)
    );
  }, [outboundHistory, outboundSearch]);
  const outboundPag = usePagination(filteredOutbound, 15);

  // v1.7.3 (L-04): count outbound dispatches today (for badge in tab label)
  const outboundToday = outboundHistory.filter((b: any) =>
    b.outbound_at && new Date(b.outbound_at).toDateString() === new Date().toDateString()
  ).length;

  // v1.7.3 (M-03): tab change handler with brief skeleton
  const handleTabChange = (t: any) => {
    if (t === activeTab) return;
    setTabLoading(true);
    setActiveTab(t as Tab);
    setTimeout(() => setTabLoading(false), 200);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="cargo" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Loading warehouse…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* v1.7.3 (M-01): WCAG 2.2 AA skip-to-content link */}
      <a href="#warehouse-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-lg">
        Skip to content
      </a>
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="cargo" />
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">Warehouse Management</h1>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">
                {user?.name} · {user?.role}
                {/* v1.7.3 (M-04): show pending decisions counter from chain stats */}
                {chainStats && Number(chainStats.pending_decisions) > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full bg-warning-100 dark:bg-warning-500/20 text-warning-700 dark:text-warning-400 text-2xs font-semibold">
                    {chainStats.pending_decisions} pending decisions
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {user?.role === 'admin' && (
              <button type="button" onClick={() => navigate('/admin')} className="btn-secondary text-xs portal-back-admin" aria-label="Back to Admin">
                <Icon.ArrowLeft className="w-4 h-4" />
                <span>Back to Admin</span>
              </button>
            )}
            <button onClick={fetchData} disabled={refreshing} className="btn-secondary text-xs hide-mobile">
              <Icon.Refresh className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>{refreshing ? 'Syncing…' : 'Refresh'}</span>
            </button>
            <IconButton label="Refresh" onClick={fetchData} disabled={refreshing} className="md:hidden">
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            <ProfileButton />
            <LogoutButton />
          </div>
        </div>
      </header>

      <main id="warehouse-main-content" className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-5">
        {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

        <section className="warehouse-ops-strip" aria-label="Warehouse operational queues">
          <button type="button" className={activeTab === 'receiving' ? 'warehouse-ops-action is-active' : 'warehouse-ops-action'} onClick={() => handleTabChange('receiving')}>
            <span className="warehouse-ops-kicker">Awaiting receipt</span>
            <strong className="nums">{pendingBatches.length}</strong>
            <span>Receiving queue</span>
          </button>
          <button type="button" className={activeTab === 'inventory' ? 'warehouse-ops-action is-active' : 'warehouse-ops-action'} onClick={() => handleTabChange('inventory')}>
            <span className="warehouse-ops-kicker">Physical stock</span>
            <strong className="nums">{inventory.length}</strong>
            <span>In warehouse</span>
          </button>
          <button type="button" className={activeTab === 'outbound' ? 'warehouse-ops-action is-active' : 'warehouse-ops-action'} onClick={() => handleTabChange('outbound')}>
            <span className="warehouse-ops-kicker">Dispatched today</span>
            <strong className="nums">{outboundToday}</strong>
            <span>{outboundHistory.length} total outbound</span>
          </button>
        </section>

        <SegmentedTabs
          items={[
            { id: 'summary', label: 'Stock Summary', icon: <Icon.Warehouse className="w-4 h-4" /> },
            { id: 'receiving', label: `Receiving (${pendingBatches.length})`, icon: <Icon.Package className="w-4 h-4" /> },
            { id: 'inventory', label: `In Stock (${inventory.length})`, icon: <Icon.Package className="w-4 h-4" /> },
            // v1.7.3 (L-04): outbound badge shows today's dispatch count when > 0
            { id: 'outbound', label: `Outbound (${outboundHistory.length}${outboundToday > 0 ? ` · ${outboundToday} today` : ''})`, icon: <Icon.Send className="w-4 h-4" /> },
            // v1.9.2: waste collection tab for zero-waste industry
            { id: 'waste', label: 'Waste Collection', icon: <Icon.Package className="w-4 h-4" /> },
            // v2.1.1 (B4 fix): Cycle Count tab — physical inventory reconciliation
            { id: 'cycle-count', label: 'Cycle Count', icon: <Icon.Database className="w-4 h-4" /> },
          ]}
          active={activeTab}
          onChange={handleTabChange}
        />

        {/* v1.7.3 (M-03): loading skeleton shown briefly when switching tabs */}
        {tabLoading ? (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-40" />
            </div>
            <div className="p-5 space-y-2">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          </Card>
        ) : (
        <>

        {/* ─── Stock Summary Tab ─── */}
        {activeTab === 'summary' && stockSummary && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Batches In Stock" value={stockSummary.summary?.total_batches_in_stock || 0} icon={<Icon.Package className="w-4 h-4" />} accent="cargo" />
              <MetricTile label="Total Weight" value={`${Number(stockSummary.summary?.total_weight_kg || 0).toFixed(1)} kg`} />
              <MetricTile label="Export Ready" value={stockSummary.summary?.export_ready || 0} />
              <MetricTile label="Factory Ready" value={stockSummary.summary?.factory_ready || 0} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <MetricTile label="Grade A" value={stockSummary.summary?.grade_a_batches || 0} />
              <MetricTile label="Grade B" value={stockSummary.summary?.grade_b_batches || 0} />
              <MetricTile label="Grade C" value={stockSummary.summary?.grade_c_batches || 0} />
            </div>

            {/* Stock by section */}
            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                <SectionHeader eyebrow="Storage" title="Stock by Warehouse Section" />
              </div>
              {(stockSummary.by_section || []).length === 0 ? (
                <div className="p-8"><EmptyState icon={<Icon.Warehouse className="w-5 h-5" />} title="No stock in any section" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Section</th><th>Batches</th><th>Weight (kg)</th></tr></thead>
                    <tbody>
                      {/* v1.7.3 (L-02): use section code as key (was array index i) */}
                      {(stockSummary.by_section || []).map((s: any) => (
                        <tr key={s.warehouse_section || `section-${Math.random()}`}>
                          <td className="text-sm font-medium">{s.warehouse_section || 'Unassigned'}</td>
                          <td className="nums">{s.batch_count}</td>
                          <td className="nums">{Number(s.weight_kg || 0).toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Warehouse sections */}
            <Card padding="none" className="overflow-hidden">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center">
                <SectionHeader eyebrow="Infrastructure" title="Warehouse Sections" subtitle="Physical storage locations with capacity tracking" />
                {/* v1.6.6: create section button (admin + clerk can create) */}
                <button onClick={() => setShowSectionSheet(true)} className="btn-primary text-xs">
                  <Icon.Package className="w-3.5 h-3.5" /> Add Section
                </button>
              </div>
              {sections.length === 0 ? (
                <div className="p-8"><EmptyState icon={<Icon.Warehouse className="w-5 h-5" />} title="No sections configured" description="Create a warehouse section to start tracking inventory by location." /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Code</th><th>Name</th><th>Zone</th><th>Capacity (kg)</th><th>Current Stock (kg)</th><th>Utilization</th><th>Cold</th><th>Action</th></tr></thead>
                    <tbody>
                      {sections.map((s: any) => {
                        const utilization = s.capacity_kg > 0 ? (Number(s.current_weight_kg || 0) / Number(s.capacity_kg) * 100).toFixed(0) : 0;
                        // v1.7.3 (L-03): color-code utilization (red >90%, yellow >75%, green otherwise)
                        const utilNum = Number(utilization);
                        const utilColor = utilNum > 90
                          ? 'text-danger-600 dark:text-danger-500 font-bold'
                          : utilNum > 75
                            ? 'text-warning-600 dark:text-warning-500 font-semibold'
                            : 'text-success-600 dark:text-success-500';
                        return (
                          <tr key={s.id}>
                            <td className="text-xs font-mono">{s.section_code}</td>
                            <td className="text-sm">{s.section_name}</td>
                            <td><StatusPill tone="info">{s.zone}</StatusPill></td>
                            <td className="nums">{Number(s.capacity_kg || 0).toLocaleString()}</td>
                            <td className="nums">{Number(s.current_weight_kg || 0).toLocaleString()}</td>
                            <td className={`nums ${utilColor}`}>{utilization}%</td>
                            <td>{s.is_cold_storage ? '❄️' : '—'}</td>
                            <td>
                              {/* v1.6.6: only admin can delete sections */}
                              {/* v1.7.3 (M-05): now opens a Sheet instead of native confirm() */}
                              {user?.role === 'admin' && (
                                <button
                                  onClick={() => setDeleteSectionSheet({ id: s.id, code: s.section_code })}
                                  className="text-danger-600 dark:text-danger-500 text-xs hover:bg-danger-50 dark:hover:bg-danger-500/10 px-2 py-1 rounded"
                                >
                                  Delete
                                </button>
                              )}
                              {user?.role !== 'admin' && (
                                <span className="text-2xs text-ink-400">Admin only</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* ─── Receiving Tab ─── */}
        {activeTab === 'receiving' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader eyebrow="Awaiting receipt" title="Receiving" subtitle="Batches that have arrived but not yet been officially received. Use the Factory Clerk portal to perform intake with quality assessment. (PENDING + DISPATCHED + IN_TRANSIT)" />
              {/* v1.7.3 (M-02): search/filter input */}
              {pendingBatches.length > 0 && (
                <div className="mt-3 relative">
                  <Icon.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <input
                    type="search"
                    value={receivingSearch}
                    onChange={(e) => { setReceivingSearch(e.target.value); receivingPag.setPage(1); }}
                    placeholder="Search by farmer name, village, or batch ID…"
                    className="input pl-9 text-sm"
                    aria-label="Search receiving batches"
                  />
                </div>
              )}
            </div>
            {pendingBatches.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No batches awaiting receipt" description="All arrived batches have been intaked." /></div>
            ) : filteredReceiving.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Search className="w-5 h-5" />} title="No matches" description={`No batches match "${receivingSearch}"`} /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Batch</th><th>Farmer</th><th>Village</th><th>Weight (kg)</th><th>Preliminary Grade</th><th>Collected</th></tr></thead>
                  <tbody>
                    {/* v1.7.3 (L-01): use pagination instead of rendering all rows at once */}
                    {receivingPag.paginatedItems.map((b: any) => (
                      <tr key={b.id}>
                        <td className="text-xs font-mono">{b.id?.slice(0, 8)}…</td>
                        <td className="text-sm">{b.farmer_name || '—'}</td>
                        <td className="text-2xs">{b.farmer_village || '—'}</td>
                        <td className="nums">{b.actual_weight || b.est_weight || '—'}</td>
                        <td><StatusPill tone={b.preliminary_quality_grade === 'A' ? 'success' : b.preliminary_quality_grade === 'B' ? 'warning' : 'danger'} dot>{b.preliminary_quality_grade || b.quality_grade || '—'}</StatusPill></td>
                        <td className="text-2xs">{b.collected_at ? new Date(b.collected_at).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* v1.7.3 (L-01): pagination footer */}
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination page={receivingPag.page} pageSize={receivingPag.pageSize} total={receivingPag.total}
                onPageChange={receivingPag.setPage} onPageSizeChange={receivingPag.setPageSize} showPageSize />
            </div>
          </Card>
        )}

        {/* ─── Inventory Tab ─── */}
        {activeTab === 'inventory' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader eyebrow="In stock" title="Warehouse Inventory" subtitle="Batches currently in the warehouse — intaked and graded, awaiting outbound dispatch" />
              {/* v1.7.3 (M-02): search/filter input */}
              {inventory.length > 0 && (
                <div className="mt-3 relative">
                  <Icon.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <input
                    type="search"
                    value={inventorySearch}
                    onChange={(e) => { setInventorySearch(e.target.value); inventoryPag.setPage(1); }}
                    placeholder="Search by farmer, batch ID, or section…"
                    className="input pl-9 text-sm"
                    aria-label="Search inventory"
                  />
                </div>
              )}
            </div>
            {inventory.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="Warehouse is empty" description="Intake batches from the Factory Clerk portal to populate inventory." /></div>
            ) : filteredInventory.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Search className="w-5 h-5" />} title="No matches" description={`No inventory matches "${inventorySearch}"`} /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Batch</th><th>Farmer</th><th>Grade</th><th>Prelim → Final</th><th>Weight (kg)</th><th>Section</th><th>Decision</th><th>Action</th></tr></thead>
                  <tbody>
                    {/* v1.7.3 (L-01): use pagination */}
                    {inventoryPag.paginatedItems.map((b: any) => (
                      <tr key={b.id}>
                        <td className="text-xs font-mono">{b.id?.slice(0, 8)}…</td>
                        <td className="text-sm">{b.farmer_name || '—'}</td>
                        <td><StatusPill tone={b.quality_grade === 'A' ? 'success' : b.quality_grade === 'B' ? 'warning' : 'danger'} dot>{b.quality_grade || '—'}</StatusPill></td>
                        <td className="text-2xs text-ink-500">
                          {b.preliminary_quality_grade || '—'} → {b.quality_grade || '—'}
                        </td>
                        <td className="nums">{b.actual_weight || b.est_weight || '—'}</td>
                        <td className="text-2xs">{b.warehouse_section || '—'}</td>
                        <td>{b.batch_decision ? <StatusPill tone={b.batch_decision === 'EXPORT' ? 'success' : b.batch_decision === 'REJECT' ? 'danger' : 'info'}>{b.batch_decision}</StatusPill> : <span className="text-ink-400 text-xs">Pending</span>}</td>
                        <td>
                          {b.batch_decision ? (
                            <button onClick={() => { setOutboundSheet(b); setOutboundForm({ destination: b.batch_decision === 'EXPORT' ? 'export_warehouse' : b.batch_decision === 'FACTORY' ? 'factory' : 'rejection_pit', container_number: '', dispatch_reference: '', notes: '' }); }} className="btn-primary text-xs">
                              <Icon.Send className="w-3 h-3" /> Dispatch
                            </button>
                          ) : (
                            <span className="text-2xs text-ink-400">Decision required</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* v1.7.3 (L-01): pagination footer */}
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination page={inventoryPag.page} pageSize={inventoryPag.pageSize} total={inventoryPag.total}
                onPageChange={inventoryPag.setPage} onPageSizeChange={inventoryPag.setPageSize} showPageSize />
            </div>
          </Card>
        )}

        {/* ─── Outbound History Tab ─── */}
        {activeTab === 'outbound' && (
          <Card padding="none" className="overflow-hidden">
            <div className="p-5 border-b border-ink-200 dark:border-ink-800">
              <SectionHeader eyebrow="Dispatch history" title="Outbound Log" subtitle="Batches that have been dispatched from the warehouse to export, factory, or rejection" />
              {/* v1.7.3 (M-02): search/filter input */}
              {outboundHistory.length > 0 && (
                <div className="mt-3 relative">
                  <Icon.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <input
                    type="search"
                    value={outboundSearch}
                    onChange={(e) => { setOutboundSearch(e.target.value); outboundPag.setPage(1); }}
                    placeholder="Search by farmer, batch ID, destination, or container…"
                    className="input pl-9 text-sm"
                    aria-label="Search outbound history"
                  />
                </div>
              )}
            </div>
            {outboundHistory.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Send className="w-5 h-5" />} title="No outbound dispatches yet" description="Dispatch batches from the Inventory tab to see them here." /></div>
            ) : filteredOutbound.length === 0 ? (
              <div className="p-8"><EmptyState icon={<Icon.Search className="w-5 h-5" />} title="No matches" description={`No outbound records match "${outboundSearch}"`} /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr><th>Batch</th><th>Farmer</th><th>Grade</th><th>Weight</th><th>Destination</th><th>Container</th><th>Reference</th><th>Dispatched</th></tr></thead>
                  <tbody>
                    {/* v1.7.3 (L-01): use pagination */}
                    {outboundPag.paginatedItems.map((b: any) => (
                      <tr key={b.id}>
                        <td className="text-xs font-mono">{b.id?.slice(0, 8)}…</td>
                        <td className="text-sm">{b.farmer_name || '—'}</td>
                        <td><StatusPill tone={b.quality_grade === 'A' ? 'success' : b.quality_grade === 'B' ? 'warning' : 'danger'} dot>{b.quality_grade || '—'}</StatusPill></td>
                        <td className="nums">{b.actual_weight || '—'}</td>
                        <td><StatusPill tone="info">{b.outbound_destination || '—'}</StatusPill></td>
                        <td className="text-2xs font-mono">{b.container_number || '—'}</td>
                        <td className="text-2xs font-mono">{b.dispatch_reference || '—'}</td>
                        <td className="text-2xs">{b.outbound_at ? new Date(b.outbound_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* v1.7.3 (L-01): pagination footer */}
            <div className="border-t border-ink-200 dark:border-ink-800">
              <Pagination page={outboundPag.page} pageSize={outboundPag.pageSize} total={outboundPag.total}
                onPageChange={outboundPag.setPage} onPageSizeChange={outboundPag.setPageSize} showPageSize />
            </div>
          </Card>
        )}

        {/* v2.1.1 (B4 fix): ─── Cycle Count Tab — physical inventory reconciliation ─── */}
        {activeTab === 'cycle-count' && (
          <Card padding="none" className="overflow-hidden"> {/* v2.1.1 (B4 fix) */}
            <div className="p-5 border-b border-ink-200 dark:border-ink-800"> {/* v2.1.1 (B4 fix) */}
              <SectionHeader eyebrow="Audit" title="Cycle Count — Physical inventory reconciliation" subtitle="Periodic physical counts vs. system balances, with variance tracking and reconciliation." icon={<Icon.Database className="w-4 h-4" />} /> {/* v2.1.1 (B4 fix) */}
              <div className="mt-3 flex flex-wrap items-center gap-2"> {/* v2.1.1 (B4 fix) */}
                {/* v2.1.1 (B4 fix): status filter buttons — DB enum is IN_PROGRESS|COUNTED|RECONCILED|APPROVED (no DRAFT/COMPLETED). */}
                {['ALL', 'IN_PROGRESS', 'COUNTED', 'RECONCILED', 'APPROVED'].map((f) => ( // v2.1.1 (B4 fix)
                  <button key={f} onClick={() => { setCycleCountFilter(f); fetchCycleCounts(f); }} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${cycleCountFilter === f ? 'bg-ink-900 text-ink-50 dark:bg-ink-50 dark:text-ink-900' : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300 hover:bg-ink-200 dark:hover:bg-ink-700'}`}> {/* v2.1.1 (B4 fix) */}
                    {f === 'ALL' ? 'All' : f === 'IN_PROGRESS' ? 'In Progress' : f.charAt(0) + f.slice(1).toLowerCase()} {/* v2.1.1 (B4 fix) */}
                  </button> /* v2.1.1 (B4 fix) */
                ))}
                <div className="flex-1" /> {/* v2.1.1 (B4 fix) */}
                {/* v2.1.1 (B4 fix): Start New Count button → opens create Sheet */}
                <button onClick={() => setShowCycleCountSheet(true)} className="btn-primary text-xs"> {/* v2.1.1 (B4 fix) */}
                  <Icon.Database className="w-3.5 h-3.5" /> Start New Count {/* v2.1.1 (B4 fix) */}
                </button>
              </div>
              {cycleCountError && <div className="mt-3"><Alert tone="danger" onClose={() => setCycleCountError(null)}>{cycleCountError}</Alert></div>} {/* v2.1.1 (B4 fix) */}
            </div>
            {cycleCountLoading ? ( // v2.1.1 (B4 fix)
              <div className="p-8 flex justify-center"><Spinner /></div> // v2.1.1 (B4 fix)
            ) : cycleCounts.length === 0 ? ( // v2.1.1 (B4 fix)
              <div className="p-8"><EmptyState icon={<Icon.Database className="w-5 h-5" />} title="No cycle counts yet" description="Start a new count to begin physical inventory reconciliation." /></div> // v2.1.1 (B4 fix)
            ) : ( // v2.1.1 (B4 fix)
              <div className="overflow-x-auto"> {/* v2.1.1 (B4 fix) */}
                <table className="data-table"> {/* v2.1.1 (B4 fix) */}
                  <thead><tr><th>Count #</th><th>Section</th><th>Date</th><th>Status</th><th>Items</th><th>Discrepancies</th><th>Actions</th></tr></thead> {/* v2.1.1 (B4 fix) */}
                  <tbody> {/* v2.1.1 (B4 fix) */}
                    {/* v2.1.1 (B4 fix): clicking a row opens the detail Sheet */}
                    {cycleCounts.map((c: any) => { // v2.1.1 (B4 fix)
                      const disc = Number(c.items_over || 0) + Number(c.items_under || 0); // v2.1.1 (B4 fix)
                      const tone: any = c.status === 'RECONCILED' || c.status === 'APPROVED' ? 'success' : c.status === 'COUNTED' ? 'info' : c.status === 'IN_PROGRESS' ? 'warning' : 'neutral'; // v2.1.1 (B4 fix)
                      return ( // v2.1.1 (B4 fix)
                        <tr key={c.id} className="cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-800/50" onClick={() => fetchCycleCountDetail(c.id)}> {/* v2.1.1 (B4 fix) */}
                          <td className="text-xs font-mono">{c.count_number || c.id?.slice(0, 8)}</td> {/* v2.1.1 (B4 fix) */}
                          <td className="text-sm">{c.warehouse_section || 'All sections'}</td> {/* v2.1.1 (B4 fix) */}
                          <td className="text-2xs">{c.count_date ? new Date(c.count_date).toLocaleDateString() : '—'}</td> {/* v2.1.1 (B4 fix) */}
                          <td><StatusPill tone={tone} dot>{(c.status || 'IN_PROGRESS').replace(/_/g, ' ')}</StatusPill></td> {/* v2.1.1 (B4 fix) */}
                          <td className="nums">{Number(c.total_items || 0)}</td> {/* v2.1.1 (B4 fix) */}
                          <td className="nums">{disc > 0 ? <span className="text-danger-600 dark:text-danger-500 font-semibold">{disc} ({Number(c.items_over || 0)}↑ / {Number(c.items_under || 0)}↓)</span> : <span className="text-success-600 dark:text-success-500">0</span>}</td> {/* v2.1.1 (B4 fix) */}
                          <td><button onClick={(e) => { e.stopPropagation(); fetchCycleCountDetail(c.id); }} className="text-xs text-cargo-600 dark:text-cargo-400 hover:underline">View</button></td> {/* v2.1.1 (B4 fix) */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* end v1.7.3 (M-03) tabLoading conditional wrapper */}
        </>
        )}

        {/* v1.9.2: Waste Collection Tab — not inside tabLoading wrapper (independent fetch) */}
        {activeTab === 'waste' && <WarehouseWasteTab />}
      </main>

      {/* ─── Outbound Dispatch Sheet ─── */}
      <Sheet open={!!outboundSheet} onClose={() => setOutboundSheet(null)} title="Dispatch Batch from Warehouse"
        footer={
          <div className="flex gap-2">
            <button onClick={() => setOutboundSheet(null)} className="btn-secondary flex-1">Cancel</button>
            {/* v1.7.3 (C-02): confirmation dialog before irreversible outbound dispatch */}
            {/* v1.7.3 (M-06): also enforce destination required in UI (button disabled if empty) */}
            <button
              onClick={() => {
                if (!outboundForm.destination) {
                  setError('Please select a destination before dispatching.');
                  return;
                }
                const batchId = outboundSheet?.id?.slice(0, 8);
                const dest = outboundForm.destination;
                if (confirm(`Confirm dispatch of batch ${batchId}… to ${dest}?\n\nThis will remove the batch from warehouse inventory and CANNOT be undone.`)) {
                  handleOutbound();
                }
              }}
              disabled={!outboundForm.destination}
              className="btn-primary flex-1"
            >
              <Icon.Send className="w-4 h-4" /> Confirm Dispatch
            </button>
          </div>
        }>
        {outboundSheet && (
          <div className="space-y-4">
            <div className="bg-ink-100 dark:bg-ink-800 rounded-lg p-3 space-y-1">
              <div className="flex justify-between"><span className="text-xs text-ink-500">Batch</span><span className="text-xs font-mono">{outboundSheet.id?.slice(0, 8)}…</span></div>
              <div className="flex justify-between"><span className="text-xs text-ink-500">Farmer</span><span className="text-xs">{outboundSheet.farmer_name}</span></div>
              <div className="flex justify-between"><span className="text-xs text-ink-500">Grade</span><StatusPill tone={outboundSheet.quality_grade === 'A' ? 'success' : 'warning'}>{outboundSheet.quality_grade}</StatusPill></div>
              <div className="flex justify-between"><span className="text-xs text-ink-500">Weight</span><span className="text-xs nums">{outboundSheet.actual_weight || outboundSheet.est_weight} kg</span></div>
              <div className="flex justify-between"><span className="text-xs text-ink-500">Decision</span><StatusPill tone="info">{outboundSheet.batch_decision}</StatusPill></div>
            </div>
            <div>
              <label htmlFor="dest" className="label">Destination *</label>
              {/* v1.7.3 (M-06): required attribute + visual indicator */}
              <select id="dest" value={outboundForm.destination} required
                onChange={(e) => setOutboundForm({ ...outboundForm, destination: e.target.value })}
                className="input">
                <option value="">— Select destination —</option>
                <option value="export_warehouse">Export Warehouse</option>
                <option value="factory">Factory (Processing)</option>
                <option value="rejection_pit">Rejection Pit</option>
                <option value="reconditioning_plant">Reconditioning Plant</option>
              </select>
              {!outboundForm.destination && (
                <p className="text-2xs text-warning-600 dark:text-warning-500 mt-1">Destination is required.</p>
              )}
            </div>
            <div>
              <label htmlFor="container" className="label">Container number (for export)</label>
              <input id="container" type="text" value={outboundForm.container_number}
                onChange={(e) => setOutboundForm({ ...outboundForm, container_number: e.target.value })}
                className="input" placeholder="e.g. CONT-2026-001" />
            </div>
            <div>
              <label htmlFor="ref" className="label">Dispatch reference</label>
              <input id="ref" type="text" value={outboundForm.dispatch_reference}
                onChange={(e) => setOutboundForm({ ...outboundForm, dispatch_reference: e.target.value })}
                className="input" placeholder="e.g. WH-OUT-2026-001" />
            </div>
            <div>
              <label htmlFor="notes" className="label">Notes</label>
              <textarea id="notes" value={outboundForm.notes}
                onChange={(e) => setOutboundForm({ ...outboundForm, notes: e.target.value })}
                className="input" rows={2} />
            </div>
            <Alert tone="warning">This will mark the batch as DISPATCHED and remove it from warehouse inventory. This action cannot be undone.</Alert>
          </div>
        )}
      </Sheet>

      {/* ─── v1.6.6: Create Warehouse Section Sheet ─── */}
      <Sheet open={showSectionSheet} onClose={() => setShowSectionSheet(false)} title="Create Warehouse Section"
        footer={
          <div className="flex gap-2">
            <button onClick={() => setShowSectionSheet(false)} className="btn-secondary flex-1">Cancel</button>
            <button onClick={handleCreateSection} disabled={!sectionForm.section_code || !sectionForm.section_name} className="btn-primary flex-1">
              <Icon.Package className="w-4 h-4" /> Create Section
            </button>
          </div>
        }>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="ws-code" className="label">Section code *</label>
              <input id="ws-code" type="text" required value={sectionForm.section_code}
                onChange={(e) => setSectionForm({ ...sectionForm, section_code: e.target.value.toUpperCase() })}
                className="input" placeholder="e.g. C-1" />
            </div>
            <div>
              <label htmlFor="ws-zone" className="label">Zone</label>
              <select id="ws-zone" value={sectionForm.zone}
                onChange={(e) => setSectionForm({ ...sectionForm, zone: e.target.value })}
                className="input">
                <option value="GENERAL">General</option>
                <option value="A">Zone A</option>
                <option value="B">Zone B</option>
                <option value="COLD">Cold Storage</option>
                <option value="EXPORT">Export Staging</option>
                <option value="BULK">Bulk Storage</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="ws-name" className="label">Section name *</label>
            <input id="ws-name" type="text" required value={sectionForm.section_name}
              onChange={(e) => setSectionForm({ ...sectionForm, section_name: e.target.value })}
              className="input" placeholder="e.g. Zone C - Rack 1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="ws-cap" className="label">Capacity (kg)</label>
              <input id="ws-cap" type="number" value={sectionForm.capacity_kg}
                onChange={(e) => setSectionForm({ ...sectionForm, capacity_kg: e.target.value })}
                className="input" placeholder="5000" />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={sectionForm.is_cold_storage}
                  onChange={(e) => setSectionForm({ ...sectionForm, is_cold_storage: e.target.checked })}
                  className="w-4 h-4 rounded border-ink-300" />
                <span>Cold storage ❄️</span>
              </label>
            </div>
          </div>
          <div>
            <label htmlFor="ws-notes" className="label">Notes</label>
            <textarea id="ws-notes" value={sectionForm.notes}
              onChange={(e) => setSectionForm({ ...sectionForm, notes: e.target.value })}
              className="input" rows={2} placeholder="Optional notes about this section" />
          </div>
        </div>
      </Sheet>

      {/* v1.7.3 (M-05): Section delete Sheet — replaces native confirm() */}
      <Sheet
        open={!!deleteSectionSheet}
        onClose={() => setDeleteSectionSheet(null)}
        title="Delete Warehouse Section"
        footer={
          <div className="flex gap-2">
            <button onClick={() => setDeleteSectionSheet(null)} className="btn-secondary flex-1">Cancel</button>
            <button
              onClick={() => deleteSectionSheet && handleDeleteSection(deleteSectionSheet.id)}
              className="btn-primary flex-1 text-danger-600"
            >
              <Icon.X className="w-4 h-4" /> Delete Section
            </button>
          </div>
        }
      >
        {deleteSectionSheet && (
          <div className="space-y-4">
            <div className="bg-danger-50 dark:bg-danger-500/10 border border-danger-200 dark:border-danger-500/30 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-danger-700 dark:text-danger-400">
                <Icon.Alert className="w-4 h-4" />
                <span className="text-sm font-semibold">Deactivate section {deleteSectionSheet.code}?</span>
              </div>
              <p className="text-xs text-ink-600 dark:text-ink-300">
                This will deactivate the section. The operation only succeeds if no batches are currently stored in it.
                Existing historical records referencing this section will be preserved.
              </p>
            </div>
            <Alert tone="warning">This action cannot be undone from the UI. To reactivate, you'll need to create a new section with the same code.</Alert>
          </div>
        )}
      </Sheet>

      {/* v2.1.1 (B4 fix): ─── Cycle Count — Create Sheet (warehouse_section / count_date / notes) ─── */}
      <Sheet open={showCycleCountSheet} onClose={() => setShowCycleCountSheet(false)} title="Start New Cycle Count" /* v2.1.1 (B4 fix) */
        footer={
          <div className="flex gap-2"> {/* v2.1.1 (B4 fix) */}
            <button onClick={() => setShowCycleCountSheet(false)} className="btn-secondary flex-1">Cancel</button> {/* v2.1.1 (B4 fix) */}
            <button onClick={handleCreateCycleCount} className="btn-primary flex-1"> {/* v2.1.1 (B4 fix) */}
              <Icon.Database className="w-4 h-4" /> Start Count {/* v2.1.1 (B4 fix) */}
            </button>
          </div>
        }>
        <div className="space-y-4"> {/* v2.1.1 (B4 fix) */}
          {/* v2.1.1 (B4 fix): warehouse section picker (optional — empty = full count) */}
          <div>
            <label htmlFor="cc-section" className="label">Warehouse section (optional)</label> {/* v2.1.1 (B4 fix) */}
            <select id="cc-section" value={cycleCountForm.warehouse_section} onChange={(e) => setCycleCountForm({ ...cycleCountForm, warehouse_section: e.target.value })} className="input"> {/* v2.1.1 (B4 fix) */}
              <option value="">All sections (full count)</option> {/* v2.1.1 (B4 fix) */}
              {sections.map((s: any) => <option key={s.id} value={s.section_code}>{s.section_code} — {s.section_name}</option>)} {/* v2.1.1 (B4 fix) */}
            </select>
          </div>
          <div>
            <label htmlFor="cc-date" className="label">Count date</label> {/* v2.1.1 (B4 fix) */}
            <input id="cc-date" type="date" value={cycleCountForm.count_date} onChange={(e) => setCycleCountForm({ ...cycleCountForm, count_date: e.target.value })} className="input" /> {/* v2.1.1 (B4 fix) */}
          </div>
          <div>
            <label htmlFor="cc-notes" className="label">Notes</label> {/* v2.1.1 (B4 fix) */}
            <textarea id="cc-notes" value={cycleCountForm.notes} onChange={(e) => setCycleCountForm({ ...cycleCountForm, notes: e.target.value })} className="input" rows={2} placeholder="Optional notes about this count" /> {/* v2.1.1 (B4 fix) */}
          </div>
          <Alert tone="info">A new count session will be created and pre-populated with current INTAKED batches from the system. Staff can then enter physical counts.</Alert> {/* v2.1.1 (B4 fix) */}
        </div>
      </Sheet>

      {/* v2.1.1 (B4 fix): ─── Cycle Count — Detail Sheet (items + add item + finalize + reconcile) ─── */}
      <Sheet open={showCycleCountDetailSheet} onClose={() => { setShowCycleCountDetailSheet(false); setCycleCountDetail(null); }} title={cycleCountDetail?.count?.count_number ? `Cycle Count ${cycleCountDetail.count.count_number}` : 'Cycle Count Detail'} /* v2.1.1 (B4 fix) */
        footer={
          <div className="flex flex-wrap gap-2"> {/* v2.1.1 (B4 fix) */}
            <button onClick={() => { setShowCycleCountDetailSheet(false); setCycleCountDetail(null); }} className="btn-secondary flex-1">Close</button> {/* v2.1.1 (B4 fix) */}
            {/* v2.1.1 (B4 fix): Finalize Count — PATCH /:id/finalize (enabled when IN_PROGRESS; transitions to COUNTED) */}
            <button onClick={handleFinalizeCycleCount} disabled={cycleCountDetail?.count?.status !== 'IN_PROGRESS'} className="btn-secondary flex-1"> {/* v2.1.1 (B4 fix) */}
              <Icon.Check className="w-4 h-4" /> Finalize Count {/* v2.1.1 (B4 fix) */}
            </button>
            {/* v2.1.1 (B4 fix): Reconcile — POST /:id/reconcile (enabled when COUNTED) */}
            <button onClick={handleReconcileCycleCount} disabled={cycleCountDetail?.count?.status !== 'COUNTED'} className="btn-primary flex-1"> {/* v2.1.1 (B4 fix) */}
              <Icon.Refresh className="w-4 h-4" /> Reconcile {/* v2.1.1 (B4 fix) */}
            </button>
          </div>
        }>
        {cycleCountDetail && ( // v2.1.1 (B4 fix)
          <div className="space-y-4"> {/* v2.1.1 (B4 fix) */}
            {/* v2.1.1 (B4 fix): summary header card */}
            <div className="bg-ink-100 dark:bg-ink-800 rounded-lg p-3 space-y-1"> {/* v2.1.1 (B4 fix) */}
              <div className="flex justify-between"><span className="text-xs text-ink-500">Count #</span><span className="text-xs font-mono">{cycleCountDetail.count?.count_number}</span></div> {/* v2.1.1 (B4 fix) */}
              <div className="flex justify-between"><span className="text-xs text-ink-500">Section</span><span className="text-xs">{cycleCountDetail.count?.warehouse_section || 'All sections'}</span></div> {/* v2.1.1 (B4 fix) */}
              <div className="flex justify-between"><span className="text-xs text-ink-500">Date</span><span className="text-xs">{cycleCountDetail.count?.count_date ? new Date(cycleCountDetail.count.count_date).toLocaleDateString() : '—'}</span></div> {/* v2.1.1 (B4 fix) */}
              <div className="flex justify-between"><span className="text-xs text-ink-500">Status</span><StatusPill tone="info">{cycleCountDetail.count?.status}</StatusPill></div> {/* v2.1.1 (B4 fix) */}
              <div className="flex justify-between"><span className="text-xs text-ink-500">Items</span><span className="text-xs nums">{cycleCountDetail.item_count || (cycleCountDetail.items || []).length}</span></div> {/* v2.1.1 (B4 fix) */}
              {cycleCountDetail.count?.reconciled_at && ( // v2.1.1 (B4 fix)
                <div className="flex justify-between"><span className="text-xs text-ink-500">Variance</span><span className="text-xs nums">{Number(cycleCountDetail.count.total_variance_kg || 0).toFixed(2)} kg · {Number(cycleCountDetail.count.items_matched || 0)} matched / {Number(cycleCountDetail.count.items_over || 0)} over / {Number(cycleCountDetail.count.items_under || 0)} under</span></div> // v2.1.1 (B4 fix)
              )}
            </div>

            {/* v2.1.1 (B4 fix): items sub-table — Batch ID | System Bags | Counted Bags | Bag Variance | System Weight | Counted Weight | Weight Variance | Notes */}
            <div>
              <h4 className="text-sm font-semibold mb-2">Counted Items ({(cycleCountDetail.items || []).length})</h4> {/* v2.1.1 (B4 fix) */}
              {(cycleCountDetail.items || []).length === 0 ? ( // v2.1.1 (B4 fix)
                <EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No items in this count" /> // v2.1.1 (B4 fix)
              ) : ( // v2.1.1 (B4 fix)
                <div className="overflow-x-auto border border-ink-200 dark:border-ink-800 rounded-lg"> {/* v2.1.1 (B4 fix) */}
                  <table className="data-table"> {/* v2.1.1 (B4 fix) */}
                    <thead><tr><th>Batch ID</th><th>System Bags</th><th>Counted Bags</th><th>Bag Variance</th><th>System Weight</th><th>Counted Weight</th><th>Weight Variance</th><th>Notes</th></tr></thead> {/* v2.1.1 (B4 fix) */}
                    <tbody> {/* v2.1.1 (B4 fix) */}
                      {cycleCountDetail.items.map((it: any) => { // v2.1.1 (B4 fix)
                        const bagVar = it.counted_bags != null ? Number(it.counted_bags) - Number(it.system_bags || 0) : null; // v2.1.1 (B4 fix)
                        const wtVar = Number(it.counted_weight_kg || 0) - Number(it.system_weight_kg || 0); // v2.1.1 (B4 fix)
                        const vTone: any = it.variance_type === 'OVER' ? 'warning' : it.variance_type === 'UNDER' ? 'danger' : 'success'; // v2.1.1 (B4 fix)
                        return ( // v2.1.1 (B4 fix)
                          <tr key={it.id}> {/* v2.1.1 (B4 fix) */}
                            <td className="text-xs font-mono">{(it.batch_id_short || it.batch_id || '').toString().slice(0, 8)}…</td> {/* v2.1.1 (B4 fix) */}
                            <td className="nums">{Number(it.system_bags || 0)}</td> {/* v2.1.1 (B4 fix) */}
                            <td className="nums">{it.counted_bags ?? '—'}</td> {/* v2.1.1 (B4 fix) */}
                            <td className="nums">{bagVar != null ? <StatusPill tone={bagVar === 0 ? 'success' : bagVar > 0 ? 'warning' : 'danger'}>{bagVar > 0 ? `+${bagVar}` : bagVar}</StatusPill> : '—'}</td> {/* v2.1.1 (B4 fix) */}
                            <td className="nums">{Number(it.system_weight_kg || 0).toFixed(2)}</td> {/* v2.1.1 (B4 fix) */}
                            <td className="nums">{it.counted_weight_kg != null ? Number(it.counted_weight_kg).toFixed(2) : '—'}</td> {/* v2.1.1 (B4 fix) */}
                            <td className="nums"><StatusPill tone={vTone}>{wtVar > 0 ? `+${wtVar.toFixed(2)}` : wtVar.toFixed(2)} kg</StatusPill></td> {/* v2.1.1 (B4 fix) */}
                            <td className="text-2xs text-ink-500">{it.notes || '—'}</td> {/* v2.1.1 (B4 fix) */}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* v2.1.1 (B4 fix): Add Item form — batch_id select + counted_bags + counted_weight_kg + notes */}
            <div className="border-t border-ink-200 dark:border-ink-800 pt-4"> {/* v2.1.1 (B4 fix) */}
              <h4 className="text-sm font-semibold mb-2">Add Item / Recount Batch</h4> {/* v2.1.1 (B4 fix) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3"> {/* v2.1.1 (B4 fix) */}
                <div className="md:col-span-2"> {/* v2.1.1 (B4 fix) */}
                  <label htmlFor="cci-batch" className="label">Batch *</label> {/* v2.1.1 (B4 fix) */}
                  <select id="cci-batch" value={cycleCountItemForm.batch_id} onChange={(e) => setCycleCountItemForm({ ...cycleCountItemForm, batch_id: e.target.value })} className="input"> {/* v2.1.1 (B4 fix) */}
                    <option value="">— Select batch —</option> {/* v2.1.1 (B4 fix) */}
                    {cycleCountBatches.map((b: any) => <option key={b.id} value={b.id}>{b.id?.slice(0, 8)}… — {b.farmer_name || 'Unknown'} ({b.warehouse_section || 'no section'})</option>)} {/* v2.1.1 (B4 fix) */}
                  </select>
                </div>
                <div>
                  <label htmlFor="cci-bags" className="label">Counted bags</label> {/* v2.1.1 (B4 fix) */}
                  <input id="cci-bags" type="number" min="0" value={cycleCountItemForm.counted_bags} onChange={(e) => setCycleCountItemForm({ ...cycleCountItemForm, counted_bags: e.target.value })} className="input" placeholder="e.g. 12" /> {/* v2.1.1 (B4 fix) */}
                </div>
                <div>
                  <label htmlFor="cci-wt" className="label">Counted weight (kg)</label> {/* v2.1.1 (B4 fix) */}
                  <input id="cci-wt" type="number" step="0.01" min="0" value={cycleCountItemForm.counted_weight_kg} onChange={(e) => setCycleCountItemForm({ ...cycleCountItemForm, counted_weight_kg: e.target.value })} className="input" placeholder="e.g. 750.50" /> {/* v2.1.1 (B4 fix) */}
                </div>
                <div className="md:col-span-2"> {/* v2.1.1 (B4 fix) */}
                  <label htmlFor="cci-notes" className="label">Notes</label> {/* v2.1.1 (B4 fix) */}
                  <input id="cci-notes" type="text" value={cycleCountItemForm.notes} onChange={(e) => setCycleCountItemForm({ ...cycleCountItemForm, notes: e.target.value })} className="input" placeholder="Optional notes" /> {/* v2.1.1 (B4 fix) */}
                </div>
              </div>
              <button onClick={handleAddCycleCountItem} disabled={!cycleCountItemForm.batch_id} className="btn-primary text-xs mt-3"> {/* v2.1.1 (B4 fix) */}
                <Icon.Package className="w-3.5 h-3.5" /> Add / Update Item {/* v2.1.1 (B4 fix) */}
              </button>
            </div>
          </div>
        )}
      </Sheet>
          <CopilotWidget userRole={(user as any)?.role || 'admin'} />
</div>
  );
};

export default WarehouseDashboard;

/* ═══════════════════════════════════════════════════════════════
 * v1.9.2 — Warehouse Waste Collection Tab
 * Shows available waste from farmer activities + collect button
 * ═══════════════════════════════════════════════════════════════ */
const WarehouseWasteTab: React.FC = () => {
  const [waste, setWaste] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/post-harvest/waste/available');
      setWaste(res.data?.waste || []);
    } catch { setWaste([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleCollect = async (activityId: string) => {
    try {
      const res = await apiClient.post(`/post-harvest/waste/${activityId}/collect`);
      setSuccess(res.data.message);
      refresh();
    } catch (err: any) { setError(err.response?.data?.error || 'Collection failed'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card padding="none" className="overflow-hidden">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Zero-Waste" title="Waste Available for Collection" subtitle="Pod husks + cocoa juice from farmer harvest activities" icon={<Icon.Package className="w-4 h-4" />} />
        </div>
        {waste.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No waste available" description="When farmers record harvest activities with waste quantities, they will appear here for collection." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Activity #</th><th>Farmer</th><th>Village</th><th>Date</th><th>Pod Husks (kg)</th><th>Cocoa Juice (L)</th><th>Action</th></tr></thead>
              <tbody>
                {waste.map(w => (
                  <tr key={w.id}>
                    <td className="text-xs font-mono">{w.activity_number}</td>
                    <td className="text-sm">{w.farmer_name}</td>
                    <td className="text-2xs">{w.village || '---'}</td>
                    <td className="text-2xs">{new Date(w.activity_date).toLocaleDateString()}</td>
                    <td className="nums">{Number(w.waste_pod_husks_kg || 0).toFixed(1)}</td>
                    <td className="nums">{Number(w.waste_cocoa_juice_l || 0).toFixed(1)}</td>
                    <td><button onClick={() => handleCollect(w.id)} className="btn-primary text-xs">Collect</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
